import { useState } from 'react';
import { X } from 'lucide-react';
import type { PaymentMethod } from '../../db/types';
import { PricingEngine } from '../../domain/pricing';
import { cashAmountCentavos } from '../../domain/payments/cashAmount';
import { CashKeypad } from './CashKeypad';

type Props = {
  totalCentavos: number;
  onComplete: (payment: {
    method: PaymentMethod;
    amountCentavos: number;
    reference?: string;
  }) => Promise<void>;
  onClose: () => void;
};
export function PaymentDialog({ totalCentavos, onComplete, onClose }: Props) {
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [cash, setCash] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [digitalConfirmed, setDigitalConfirmed] = useState(false);
  const amountCentavos = method === 'CASH' ? cashAmountCentavos(cash) : totalCentavos;
  const changeCentavos = Math.max(0, amountCentavos - totalCentavos);
  const complete = async () => {
    try {
      setError('');
      if (method !== 'CASH' && !digitalConfirmed)
        throw new Error(
          'Confirm that you verified the payment in the merchant wallet before completing the order.',
        );
      if (amountCentavos < totalCentavos) throw new Error('Payment is less than the amount due.');
      await onComplete({ method, amountCentavos, reference: reference.trim() || undefined });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save payment.');
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-2 sm:p-5">
      <div className="flex max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[94dvh]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Payment
            </p>
            <h2 className="text-xl font-black text-[#003B70]">Complete payment</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close payment"
            className="grid h-14 w-14 place-items-center rounded-xl bg-slate-100"
          >
            <X />
          </button>
        </div>
        <div className="min-h-0 overflow-auto p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-[#003B70] p-4 text-white sm:col-span-2 sm:p-5">
              <p className="text-sm font-extrabold uppercase tracking-[0.16em] opacity-80">Total</p>
              <p className="payment-total-value mt-1 text-5xl font-black tabular-nums sm:text-6xl">
                {PricingEngine.formatCentavos(totalCentavos)}
              </p>
            </div>
            <div className="rounded-2xl border-2 border-[#9EC6E9] bg-[#F4F9FE] p-4" aria-live="polite">
              <p className="text-sm font-extrabold uppercase tracking-wide text-[#00549C]">Amount Paid / Bayad</p>
              <p className="payment-paid-value mt-1 text-4xl font-black tabular-nums text-[#003B70] sm:text-5xl">
                {PricingEngine.formatCentavos(amountCentavos)}
              </p>
            </div>
            <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-100 p-4" aria-live="polite">
              <p className="text-sm font-extrabold uppercase tracking-wide text-emerald-800">Change / Sukli</p>
              <p className="payment-change-value mt-1 text-4xl font-black tabular-nums text-emerald-800 sm:text-5xl">
                {PricingEngine.formatCentavos(changeCentavos)}
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-2">
            {(['CASH', 'GCASH', 'MAYA'] as PaymentMethod[]).map((option) => (
              <button
                key={option}
                onClick={() => setMethod(option)}
                className={`min-h-[64px] rounded-xl border text-sm font-black ${method === option ? 'border-[#00549C] bg-[#DCEEFF] text-[#003B70]' : 'border-slate-200 bg-white text-slate-600'}`}
              >
                {option}
              </button>
            ))}
          </div>
          {method === 'CASH' ? (
            <div className="mt-5">
              <div className="mb-3 grid grid-cols-5 gap-2">
                <button
                  type="button"
                  onClick={() => setCash((totalCentavos / 100).toFixed(2))}
                  className="min-h-[58px] rounded-xl border border-[#9EC6E9] bg-[#DCEEFF] px-2 text-sm font-black text-[#00549C]"
                >
                  Exact
                </button>
                {[100, 200, 500, 1000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setCash(String(amount))}
                    className="min-h-[58px] rounded-xl border border-slate-200 bg-white px-2 text-sm font-black text-[#003B70]"
                  >
                    ₱{amount.toLocaleString()}
                  </button>
                ))}
              </div>
              <CashKeypad value={cash} onChange={setCash} />
            </div>
          ) : (
            <div className="mt-5">
              <div className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-600">
                Customer pays exactly {PricingEngine.formatCentavos(totalCentavos)} using {method}.
              </div>
              <label className="mt-4 block text-sm font-bold text-slate-700">
                Payment reference
                <input
                  autoFocus
                  value={reference}
                  onChange={(e) => {
                    setReference(e.target.value);
                    setDigitalConfirmed(false);
                  }}
                  placeholder="Enter reference number"
                  className="mt-2 h-14 w-full rounded-xl border border-slate-200 px-4 text-lg font-semibold outline-none focus:border-[#00549C]"
                />
              </label>
              <label className="mt-4 flex min-h-14 items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 text-sm font-bold text-amber-800">
                <input
                  type="checkbox"
                  checked={digitalConfirmed}
                  onChange={(e) => setDigitalConfirmed(e.target.checked)}
                  className="h-5 w-5 accent-[#00549C]"
                />{' '}
                I verified the payment was received in the merchant wallet and the reference is
                correct.
              </label>
            </div>
          )}
          {error && (
            <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600">{error}</p>
          )}
        </div>
        <div className="shrink-0 border-t border-slate-100 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-5">
          <button
            onClick={() => void complete()}
            disabled={
              amountCentavos < totalCentavos ||
              (method !== 'CASH' && (!reference.trim() || !digitalConfirmed))
            }
            className="min-h-[68px] w-full rounded-xl bg-[#00549C] text-xl font-black text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            SAVE & COMPLETE ORDER
          </button>
        </div>
      </div>
    </div>
  );
}
